// ─── CSV parser (minimal, dependency-free) ───────────────────────────────────
//
// Handles common CSV quirks: quoted fields with embedded commas, escaped
// quotes ("" → "), CRLF line endings. NOT a full RFC4180 parser — for that
// use papaparse if needs grow.

export interface CsvParseResult {
  headers: string[];
  rows: Record<string, string>[];
  /** Per-row line numbers (1-based, header is row 1). */
  lineNumbers: number[];
}

/** Parse CSV text into objects keyed by header row. */
export function parseCsv(text: string): CsvParseResult {
  const lines = splitLines(text);
  if (lines.length === 0) {
    return { headers: [], rows: [], lineNumbers: [] };
  }
  const headers = splitCsvLine(lines[0]).map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  const lineNumbers: number[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (cells[j] ?? '').trim();
    }
    rows.push(row);
    lineNumbers.push(i + 1);
  }
  return { headers, rows, lineNumbers };
}

function splitLines(text: string): string[] {
  // Split on \n but respect lines inside quoted cells
  const lines: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      // Check for escaped "" inside quoted field
      if (inQuotes && text[i + 1] === '"') {
        buf += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      buf += ch;
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      // Handle \r\n as one line break
      if (ch === '\r' && text[i + 1] === '\n') i++;
      lines.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf) lines.push(buf);
  return lines;
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        buf += '"';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  cells.push(buf);
  return cells;
}

/** Produce a downloadable CSV template + sample row for the contact importer. */
export const CONTACT_CSV_TEMPLATE =
  'name,email,phone,user_type,department,jobTitle,employee_id,site,startDate\n' +
  'Andi Wijaya,andi@rascalrepublic.com,0812-3456-7890,employee,Operations,Supervisor,RR-2025-001,Lombok HQ,2024-01-15\n' +
  'Siti Marlina,siti.lapangan@gmail.com,0813-9876-543,employee,Field Ops,Crew Lead,RR-2025-047,Rinjani Site,2025-03-01';
